import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Upload } from "antd";
import { AppModal, Input } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";
import type { HealthRecord } from "@/shared/types";
import { cn } from "@/shared/lib/utils";
import { parseHealthFile, ACCEPTED_EXTENSIONS, type ParsedHealthData } from "@/features/patient/records/utils/file-parsers";
import { uploadMedicalZip, UploadRequestError } from "@/features/patient/records/api/dataPipelineApi";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { toastWarning } from "@/shared/lib/toast";
import { matchesAccept } from "@/shared/lib/fileAccept";

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  status: "staged" | "uploading" | "processing" | "parsing" | "complete" | "error";
  progress: number;
  parsedData?: ParsedHealthData;
  errorMessage?: string;
}

// Matches the "under 10 MB" limit stated in the modal's helper text — and
// the backend's own MAX_UPLOAD_SIZE_BYTES (upload_validation.py), which is
// the real enforcement point; this is just an early, friendlier rejection
// before the file is ever sent.
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

// pdf/jpg/jpeg/png/tif/tiff/bmp/heic/heif/webp/gif/ico — the backend's
// document_extraction pipeline (Tesseract OCR + Textract fallback) supports
// these directly, routed through the SAME /upload endpoint zip uploads
// already use (see upload_router.py's store_and_extract_document). Mirrors
// the backend's own SUPPORTED_DOCUMENT_EXTENSIONS (archive_utils.py)
// exactly — keep both in sync. There's a documented past bug in this exact
// codebase where this list silently drifted from the backend's, and the
// frontend rejected files the backend would have accepted with no error
// shown to the user — double-check this stays in sync whenever the backend
// list changes.
const DIRECT_EXTRACTION_EXTENSIONS = [
  "pdf", "jpg", "jpeg", "png", "tif", "tiff", "bmp",
  "heic", "heif", "webp", "gif", "ico",
];
const DIRECT_CCDA_EXTENSIONS = ["xml", "ccd"];

// Every format the backend's /upload endpoint accepts (see upload_validation.py's
// ALLOWED_UPLOAD_TYPES, which this must be kept in sync with): zip (Epic/Athena
// export), the direct-extraction formats above, plus csv/json (stored, not
// parsed — see startLocalParse). doc/docx deliberately excluded — medical
// records don't come in Word-document format.
const ALLOWED_EXTENSIONS = new Set(
  [...`${ACCEPTED_EXTENSIONS},.zip`.split(","), ...DIRECT_EXTRACTION_EXTENSIONS.map((ext) => `.${ext}`)]
    .map((ext) => ext.trim().replace(/^\./, "").toLowerCase())
);

// The <Upload> element's accept list — the same formats as ALLOWED_EXTENSIONS.
const UPLOAD_ACCEPT = `${ACCEPTED_EXTENSIONS},.zip,${DIRECT_EXTRACTION_EXTENSIONS.map((ext) => `.${ext}`).join(",")}`;

function isDirectExtractionFile(name: string): boolean {
  return DIRECT_EXTRACTION_EXTENSIONS.includes(getExtension(name));
}

function isDirectCcdaFile(name: string): boolean {
  return DIRECT_CCDA_EXTENSIONS.includes(getExtension(name));
}

function isZipFile(name: string): boolean {
  return name.toLowerCase().endsWith(".zip");
}

function isBackendUploadFile(name: string): boolean {
  return isZipFile(name) || isDirectExtractionFile(name) || isDirectCcdaFile(name);
}

// Which data-modal tab a DIRECT (non-zip) upload's own records will land in
// once processed — null for a zip, deliberately: a zip's contents are NOT
// knowable ahead of time. Only a direct, unzipped upload has one guaranteed
// outcome: a direct-extraction file always runs through OCR/Textract —
// "View PDF Data" — and a direct C-CDA file is validated before the upload
// is even accepted — "Parsed Data".
function directUploadCategory(name: string): "structured" | "pdf" | null {
  if (isZipFile(name)) return null;
  return isDirectExtractionFile(name) ? "pdf" : "structured";
}

function getExtension(name: string): string {
  return name.split(".").pop()?.toLowerCase() || "";
}

function formatFileSizeMB(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function buildHealthRecordsForFile(file: UploadedFile): HealthRecord[] {
  if (file.parsedData && file.parsedData.records.length > 0) {
    return file.parsedData.records.map((record, index) => ({
      id: `${file.id}-${index}`, type: record.type || "lab_result",
      title: record.title || file.name.replace(/\.[^/.]+$/, ""),
      date: record.date || new Date().toISOString().split("T")[0],
      provider: record.provider || "Uploaded Document", facility: record.facility || "Self Uploaded",
      summary: record.summary || "Document uploaded and processed successfully.",
      plainLanguageSummary: "This document has been uploaded and processed.",
      status: record.status || "normal",
      details: record.details || [{ label: "Source Format", value: file.parsedData?.format?.toUpperCase() || "Document", status: "normal" as const, explanation: "The format of the uploaded file." }],
    })) as HealthRecord[];
  }
  return [{ id: `new-${file.id}`, type: "lab_result" as const, title: file.name.replace(/\.[^/.]+$/, ""), date: new Date().toISOString().split("T")[0], provider: "Uploaded Document", facility: "Self Uploaded", summary: "Document uploaded and processed successfully.", plainLanguageSummary: "This document has been uploaded and is ready for review.", status: "normal" as const, details: [{ label: "Document Type", value: file.type, status: "normal" as const, explanation: "The type of document that was uploaded." }] }] as HealthRecord[];
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function getFileIcon(type: string, name: string) {
  const ext = name.split(".").pop()?.toLowerCase();
  if (ext === "zip" || type.includes("zip")) return "lucide:archive";
  if (ext === "json" || type.includes("json")) return "lucide:file-json";
  if (ext === "xml" || type.includes("xml")) return "lucide:file-code";
  if (ext === "csv" || type.includes("csv")) return "lucide:file-text";
  if (ext === "sql" || type.includes("sql")) return "lucide:database";
  if (["tif", "tiff"].includes(ext || "") || type.includes("tiff")) return "lucide:file-image";
  if (type.startsWith("image/")) return "lucide:image";
  return "lucide:file-text";
}

const getTypeFromExtension = (filename: string): string => {
  const ext = filename.split(".").pop()?.toLowerCase();
  const typeMap: Record<string, string> = { json: "application/json", xml: "application/xml", csv: "text/csv", sql: "application/sql", tif: "image/tiff", tiff: "image/tiff", pdf: "application/pdf", jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", bmp: "image/bmp", zip: "application/zip" };
  return typeMap[ext || ""] || "application/octet-stream";
};

/**
 * "+ Add" trigger's modal for the general medical-records upload path —
 * same shell/title-field pattern as ImagingUploadModal (enter a title,
 * stage one or more files, then Upload), carrying over UploadRecords'
 * existing multi-format handling (zip/CCDA/direct-extraction go through the
 * real backend /upload endpoint and get polled via UploadHistoryContext;
 * csv/json are parsed entirely client-side, see startLocalParse) unchanged.
 */
export function RecordsUploadModal({
  open,
  onClose,
  onUpload,
}: {
  open: boolean;
  onClose: () => void;
  onUpload: (records: HealthRecord[]) => void;
}) {
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;
  const navigate = useNavigate();
  const { addZipUpload, zipUploads, updateZipEntry, recoverUploadId } = useUploadHistory();

  const [title, setTitle] = useState("");
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  // Starts fresh every time the modal is opened — matches ImagingUploadModal's
  // own "closing always discards whatever was staged" behavior.
  useEffect(() => {
    if (open) {
      setTitle("");
      setFiles([]);
    }
  }, [open]);

  // Mirrors each in-flight upload's real backend status onto its local file
  // card — see UploadRecords.tsx's original version of this effect for the
  // full reasoning (unchanged here).
  useEffect(() => {
    setFiles((prev) => {
      let changed = false;
      const next = prev.map((f) => {
        if (f.status !== "uploading" && f.status !== "processing") return f;
        const entry = zipUploads.find((z) => z.id === f.id);
        if (!entry || entry.status === "processing") return f;
        changed = true;
        if (entry.status === "complete") {
          window.setTimeout(() => {
            setFiles((p) => p.filter((x) => x.id !== f.id));
          }, 1200);
          return { ...f, status: "complete" as const, progress: 100 };
        }
        return { ...f, status: "error" as const, errorMessage: entry.errorMessage || "Processing failed." };
      });
      return changed ? next : prev;
    });
  }, [zipUploads]);

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); }, []);

  const stageFile = (file: File) => {
    const id = Math.random().toString(36).substr(2, 9);
    const base = {
      id, file, name: file.name, size: file.size,
      type: file.type || getTypeFromExtension(file.name),
      progress: 0,
    };

    const extension = getExtension(file.name);
    if (!ALLOWED_EXTENSIONS.has(extension)) {
      setFiles((prev) => [...prev, {
        ...base, status: "error",
        errorMessage: `Unsupported file format ".${extension || "unknown"}". Allowed formats: ${[...ALLOWED_EXTENSIONS].join(", ")}.`,
      }]);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFiles((prev) => [...prev, {
        ...base, status: "error",
        errorMessage: `File is too large (${formatFileSizeMB(file.size)}). Maximum allowed size is ${formatFileSizeMB(MAX_FILE_SIZE_BYTES)}.`,
      }]);
      return;
    }

    setFiles((prev) => [...prev, { ...base, status: "staged" }]);
  };

  const startDocumentUpload = (staged: UploadedFile, preferredTab?: "structured" | "pdf") => {
    setFiles((prev) => prev.map((f) => f.id === staged.id ? { ...f, status: "uploading" } : f));

    addZipUpload({
      id: staged.id,
      fileName: staged.name,
      title: title.trim() || undefined,
      uploadedAt: Date.now(),
      status: "processing",
      processingStage: "uploading",
      processingProgress: 0,
      preferredTab,
    });

    const clientRequestId = crypto.randomUUID();

    let uploadRampProgress = 0;
    const uploadRampTimer = window.setInterval(() => {
      uploadRampProgress = Math.min(9, uploadRampProgress + 1.5);
      updateZipEntry(staged.id, { processingProgress: uploadRampProgress });
    }, 200);

    uploadMedicalZip(staged.file, actingAsProfileId, undefined, clientRequestId, title).then((uploadResult) => {
      window.clearInterval(uploadRampTimer);
      setFiles((prev) => prev.map((f) => f.id === staged.id ? { ...f, status: "processing" } : f));
      updateZipEntry(staged.id, {
        uploadId: uploadResult.upload_id,
        status: "processing",
        parsingIssues: uploadResult.parsing_issues ?? undefined,
        processingStage: "queued",
        processingProgress: 10,
      });
      window.setTimeout(() => updateZipEntry(staged.id, { processingProgress: 20 }), 400);

      if (uploadResult.parsing_issues && uploadResult.parsing_issues.length > 0) {
        const names = uploadResult.parsing_issues.map((issue) => issue.source_file).filter(Boolean).join(", ");
        const count = uploadResult.parsing_issues.length;
        toastWarning(
          `${count} file${count === 1 ? "" : "s"} in "${staged.name}" ${count === 1 ? "isn't" : "aren't"} a supported format and will be skipped: ${names}`,
          7000,
        );
      }
    }).catch((error) => {
      window.clearInterval(uploadRampTimer);
      if (error instanceof UploadRequestError && error.networkError) {
        recoverUploadId(staged.id, clientRequestId, actingAsProfileId);
        return;
      }
      const message = error instanceof Error ? error.message : "Upload failed";
      setFiles((prev) => prev.map((f) => f.id === staged.id ? { ...f, status: "error", errorMessage: message } : f));
      updateZipEntry(staged.id, { status: "failed", errorMessage: message });
    });
  };

  const startLocalParse = (staged: UploadedFile) => {
    setFiles((prev) => prev.map((f) => f.id === staged.id ? { ...f, status: "uploading" } : f));
    let progress = 0;
    const uploadInterval = window.setInterval(() => {
      progress += Math.random() * 25;
      if (progress >= 100) {
        progress = 100;
        window.clearInterval(uploadInterval);
        setFiles((prev) => prev.map((f) => f.id === staged.id ? { ...f, progress: 100, status: "parsing" } : f));
        parseHealthFile(staged.file).then((parsedData) => {
          const hasError = parsedData.parseErrors.length > 0 && parsedData.records.length === 0;
          const finished: UploadedFile = { ...staged, status: hasError ? "error" : "complete", parsedData, errorMessage: parsedData.parseErrors.join("; ") };
          setFiles((prev) => prev.map((f) => f.id === staged.id ? finished : f));
          if (!hasError) onUpload(buildHealthRecordsForFile(finished));
        }).catch((error) => {
          setFiles((prev) => prev.map((f) => f.id === staged.id ? { ...f, status: "error", errorMessage: error.message } : f));
        });
      } else {
        setFiles((prev) => prev.map((f) => f.id === staged.id ? { ...f, progress } : f));
      }
    }, 150);
  };

  const handleUpload = () => {
    const toUpload = files.filter((f) => f.status === "staged");
    if (toUpload.length === 0) return;

    const allBackendUploads = toUpload.every((f) => isBackendUploadFile(f.name));

    const directCategories = new Set(
      toUpload.filter((f) => isBackendUploadFile(f.name)).map((f) => directUploadCategory(f.name))
    );
    const preferredTab =
      directCategories.size === 1 && !directCategories.has(null)
        ? ([...directCategories][0] as "structured" | "pdf")
        : undefined;

    for (const file of toUpload) {
      if (isBackendUploadFile(file.name)) startDocumentUpload(file, preferredTab);
      else startLocalParse(file);
    }

    // A batch made up entirely of zip/document/CCDA files has nothing left
    // to watch in this modal once submitted — close it and take the patient
    // straight to "Your Uploads" to watch it process live. A batch that
    // includes a plain csv/json file stays open here: that format is parsed
    // entirely locally and never reaches "Your Uploads", so closing would
    // tear down its still-running progress for nothing.
    if (allBackendUploads) {
      setFiles([]);
      onClose();
      navigate("/patient/documents");
    }
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const clearFiles = () => setFiles([]);

  const isRemovable = (status: UploadedFile["status"]) =>
    status === "staged" || status === "error" || status === "complete";

  const hasStagedFiles = files.some((f) => f.status === "staged");

  // The <Upload> overlay covering the drop zone already stages dropped files
  // through beforeUpload, and the drop event then bubbles up to here —
  // staging those again would add every dropped file twice. But Upload
  // silently discards dropped files outside its accept list (the file
  // picker just hides them), so those are staged here instead, where
  // stageFile turns each into an "Unsupported file format" error row.
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    Array.from(e.dataTransfer.files)
      .filter((file) => !matchesAccept(file, UPLOAD_ACCEPT))
      .forEach(stageFile);
  };

  return (
    <AppModal
      open={open}
      onOpenChange={(next) => { if (!next) onClose(); }}
      width={560}
      hideCloseButton
      className="[&_.ant-modal-content]:!rounded-3xl [&_.ant-modal-content]:!p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-base font-extrabold uppercase tracking-wide text-[#0A2947]">Medical Records</h2>
        <Button
          variant="ghost"
          onClick={onClose}
          aria-label="Close"
          className="!flex !h-8 !w-8 !shrink-0 !items-center !justify-center !rounded-full !p-0 !text-[#0A2947] hover:!bg-[#F3F4F6] hover:!text-[#0A2947]"
        >
          <Icon icon="lucide:x" className="h-5 w-5" />
        </Button>
      </div>

      <div className="mt-5">
        <label htmlFor="records-upload-title" className="text-xs font-extrabold uppercase tracking-wide text-[#0A2947]">
          Title
        </label>
        <Input
          id="records-upload-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter title"
          className="!mt-2 !h-11 !rounded-xl"
        />
      </div>

      <div className="mt-5">
        <p className="text-xs font-extrabold uppercase tracking-wide text-[#0A2947]">Select Files</p>

        {/* Same shell as ImagingUploadModal's DICOM drop zone — icon +
            primary line inside the box, one caption below it in a flex/gap
            wrapper (a margin here can read as collapsed against the box, gap
            can't) — just the icon and copy swapped for this upload's own
            formats. */}
        <div className="mt-2 flex flex-col gap-6">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              "relative flex min-h-[160px] flex-col items-center justify-center rounded-xl border-2 border-dashed bg-[#FCFCFC] transition-all",
              isDragging ? "border-[#F36F8F] bg-[#FFF4F7]" : "border-[#F7B4C3] hover:border-[#F36F8F]",
            )}
          >
            <Upload
              multiple
              accept={UPLOAD_ACCEPT}
              showUploadList={false}
              beforeUpload={(file) => { stageFile(file); return false; }}
              className="absolute inset-0 [&_.ant-upload]:!block [&_.ant-upload]:!h-full [&_.ant-upload]:!w-full [&_.ant-upload]:!cursor-pointer"
            >
              <div className="h-full w-full" />
            </Upload>
            <Icon icon="lucide:file-text" className="h-8 w-8 text-[#F36F8F]" />
            <p className="mt-2 text-sm font-extrabold text-[#0A2947]">
              Drop file or <span className="text-[#F17893]">Browse</span>
            </p>
          </div>
          <p className="text-xs text-[#9CA3AF]">
            We support Epic/Athena ZIP exports, C-CDA .xml/.ccd files, plus individual PDF and image files (pdf, jpg, png, tif, bmp, csv, json or zip), up to 10 MB each.
          </p>
        </div>

        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((file) => {
              const fileIconName = getFileIcon(file.type, file.name);
              return (
                <div key={file.id} className="rounded-2xl border border-[#E5E7EB] bg-white px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F7F7F7]">
                      <Icon icon={fileIconName} className="h-4 w-4 text-[#0A2947]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-extrabold text-[#0A2947]">{file.name}</p>
                      <p className="text-xs text-[#B0B0B8]">
                        {formatFileSize(file.size)}
                        {file.status === "complete" && file.parsedData && (
                          <> · {file.parsedData.records.length || 1} record{(file.parsedData.records.length || 1) > 1 ? "s" : ""}</>
                        )}
                      </p>
                      {file.status === "staged" && (
                        <p className="mt-1 text-xs font-medium text-[#9CA3AF]">Ready — press Upload to send</p>
                      )}
                      {file.status === "uploading" && (
                        <div className="mt-1.5">
                          <div className="mb-0.5 flex items-center justify-between">
                            <span className="text-xs font-semibold text-[#E4002B]">Uploading {Math.round(file.progress)}%</span>
                          </div>
                          <div className="h-1.5 w-full rounded-full bg-[#F3F4F6]">
                            <div className="h-1.5 rounded-full bg-[#E4002B]/50 transition-all" style={{ width: `${file.progress}%` }} />
                          </div>
                        </div>
                      )}
                      {file.status === "processing" && (
                        <div className="mt-1 flex items-center gap-1 text-xs font-semibold text-[#E4002B]"><Icon icon="lucide:loader-2" className="h-3 w-3 animate-spin" />Processing...</div>
                      )}
                      {file.status === "parsing" && (
                        <div className="mt-1 flex items-center gap-1 text-xs font-semibold text-[#E4002B]"><Icon icon="lucide:loader-2" className="h-3 w-3 animate-spin" />Parsing...</div>
                      )}
                      {file.status === "complete" && (
                        <div className="mt-1 flex items-center gap-1 text-xs text-[#16A34A]"><Icon icon="lucide:check-circle-2" className="h-3 w-3" />Ready to import</div>
                      )}
                      {file.status === "error" && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-[#DC2626]"><Icon icon="lucide:alert-circle" className="h-3 w-3" />{file.errorMessage || "Failed to process file"}</p>
                      )}
                    </div>
                    {isRemovable(file.status) && (
                      <Button
                        variant="ghost"
                        onClick={() => removeFile(file.id)}
                        className="!flex !h-7 !w-7 !shrink-0 !items-center !justify-center !rounded-full !p-0 hover:!bg-[#F3F4F6]"
                      >
                        <Icon icon="lucide:x" className="h-4 w-4 text-[#9CA3AF]" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cancel / Upload — hidden until something is actually staged, per
          the DICOM modal's own precedent: no buttons by default, both
          appear once a file is added. */}
      {hasStagedFiles && (
        <div className="mt-6 grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={clearFiles}
            className="!h-11 !rounded-xl !border-2 !border-[#7B6D9B] !bg-white !text-sm !font-extrabold !text-[#0A2947] hover:!border-[#7B6D9B] hover:!bg-[#F7F7F7] hover:!text-[#0A2947]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            className="!h-11 !rounded-xl !bg-[#F17893] !text-sm !font-extrabold !text-white hover:!bg-[#E85D7C]"
          >
            Upload
          </Button>
        </div>
      )}
    </AppModal>
  );
}
